import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { colorAccent, colorForeground } from "../rsc/colors";
import { useMobile } from "../context/MobileContext";

interface Props {
    hasPrices: boolean;
}

function getWIBDate(): Date {
    // WIB = UTC+7
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    return new Date(utc + 7 * 3600000);
}

function isOffMarketHours(): boolean {
    const wib = getWIBDate();
    return wib.getHours() < 8;
}

function getTimeUntil8AM(): { hours: number; minutes: number; seconds: number } {
    const wib = getWIBDate();
    const target = new Date(wib);
    target.setHours(8, 0, 0, 0);

    if (wib >= target) {
        // Already past 8AM WIB today — next 8AM is tomorrow
        target.setDate(target.getDate() + 1);
    }

    const diff = target.getTime() - wib.getTime();
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return { hours, minutes, seconds };
}

function pad(n: number): string {
    return n.toString().padStart(2, "0");
}

function getWIBFormattedDate(locale: string): string {
    const wib = getWIBDate();
    const lang = locale.startsWith("en") ? "en-GB" : "id";
    return wib.toLocaleDateString(lang, { weekday: "long", day: "numeric", month: "long" });
}

export function MarketCountdown() {
    const { t, i18n } = useTranslation();
    const [countdown, setCountdown] = useState(getTimeUntil8AM());
    const [offHours, setOffHours] = useState(isOffMarketHours());

    const isMobile = useMobile();

    useEffect(() => {
        const interval = setInterval(() => {
            setCountdown(getTimeUntil8AM());
            setOffHours(isOffMarketHours());
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const isLive = !offHours;
    const formattedDate = getWIBFormattedDate(i18n.language);

    if (isLive) {
        // 8AM – 23:59 WIB: green dot + "Live Prices — Wednesday, 1 April"
        return (
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flex: 1,
                justifyContent: "center",
                minWidth: 0,
            }}>
                <p style={{
                    margin: 0,
                    color: colorForeground,
                    fontSize: 14,
                    fontFamily: "'Public Sans', system-ui, sans-serif",
                    fontWeight: 500,
                    textAlign: "center",
                    lineHeight: 1.3,
                }}>
                    <span style={{
                        display: "inline-block",
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "#22c55e",
                        marginRight: 6,
                        verticalAlign: "middle",
                        boxShadow: "0 0 6px rgba(34,197,94,0.5)",
                    }} />
                    {t("market_live_message", { date: formattedDate })}
                </p>
            </div>
        );
    }

    // 00:00 – 7:59 WIB: orange dot + stacked message + countdown
    return (
        <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flex: 1,
            justifyContent: "center",
            minWidth: 0
        }}>
            <p style={{
                margin: 0,
                color: colorForeground,
                fontSize: isMobile ? 12 : 14,
                fontFamily: "'Public Sans', system-ui, sans-serif",
                fontWeight: 500,
                textAlign: "center",
                lineHeight: 1.3,
            }}>
                <span style={{
                    display: "inline-block",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: colorAccent,
                    marginRight: 6,
                    verticalAlign: "middle",
                }} />
                {t("market_closed_message")}
            </p>
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginTop: 6,
            }}>
                <span style={{
                    fontFamily: "'Public Sans', system-ui, sans-serif",
                    fontSize: isMobile ? 11 : 14,
                    fontWeight: 500,
                    color: colorAccent,
                }}>
                    {t("time_to_live")}
                </span>
                <span style={{
                    fontFamily: "'Geist Mono', 'SF Mono', monospace",
                    fontSize: isMobile ? 13 : 15,
                    fontWeight: 700,
                    color: colorAccent,
                    letterSpacing: "0.05em",
                    background: "rgba(230,115,22,0.1)",
                    padding: "2px 8px",
                    borderRadius: 4,
                }}>
                    {pad(countdown.hours)}:{pad(countdown.minutes)}:{pad(countdown.seconds)}
                </span>
            </div>
        </div>
    );
}
